import { useState, useContext } from "react";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { mainContext } from "@ts/constants";
import {
    IGoals,
    IMainContext,
    TCommonRowData,
    TOverviewGrid,
} from "@ts/common-types";
import { useSelector } from "react-redux";
import { GoalActionCellRenderer, GoalStatusCellRenderer } from "@/components/cell-renderers/AllCellRenderers";
import { numericValueFormatter } from "@/ts/utils";
import AgGridLayout from "@/components/wrappers/layouts/AgGridLayout";

const GoalsGrid: React.FC<TOverviewGrid> = ({ gridApi, setGridApi }) => {
    const { goalsGridLoading } = useContext<IMainContext>(mainContext);
    const goalData = useSelector(
        (state: IGoals) => state.goalData.value
    ) as unknown as TCommonRowData;

    const [columnDefs] = useState<ColDef[]>([
        {
            field: "goal_name",
            headerName: "Goal Name",
            filter: "agTextColumnFilter",
            sortable: true,
            maxWidth: 300
        },
        {
            field: "goal_active_status",
            headerName: "State",
            sortable: true,
            hide: true,
            filter: "agSetColumnFilter",
            filterParams: {
                valueFormatter: function (params: ValueFormatterParams) {
                    return params.value === "true" ? "Active" : "Archived"
                },
            },
            valueFormatter: (params: ValueFormatterParams) => {
                return params.value ? "Active" : "Archived"
            },
            maxWidth: 300
        },
        {
            field: "data_level",
            headerName: "Data Level",
            filter: "agTextColumnFilter",
            sortable: true,
            hide: true,
            maxWidth: 200
        },
        {
            field: "metric_level",
            headerName: "Metric Unit",
            filter: "agTextColumnFilter",
            sortable: true,
            maxWidth: 150
        },
        {
            field: "metric_level_value",
            headerName: "Target",
            valueFormatter: numericValueFormatter,
            sortable: true,
            maxWidth: 200,
            cellRendererParams: {
                decimalPlace: 2,
            },
        },
        {
            field: "goal_result",
            headerName: "Achievement",
            valueFormatter: numericValueFormatter,
            sortable: true,
            maxWidth: 200,
            cellRendererParams: {
                decimalPlace: 2,
            },
        },
        {
            field: "total_percent_achieved",
            headerName: "Total Goal Achieved",
            sortable: true,
            hide: true,
            valueFormatter: numericValueFormatter,
            maxWidth: 200,
            cellRendererParams: {
                decimalPlace: 1,
                hasPercent: true
            },
        },
        {
            field: "pro_rated_percent_achieved",
            headerName: "% Achievement",
            sortable: true,
            valueFormatter: numericValueFormatter,
            maxWidth: 200,
            cellRendererParams: {
                decimalPlace: 1,
                hasPercent: true
            },
        },
        {
            field: "goal_priority",
            headerName: "Goal Priority",
            hide: true,
            filter: "agSetColumnFilter",
            sortable: true,
            maxWidth: 200
        },
        {
            field: "",
            headerName: "Goal Status",
            filter: false,
            sortable: true,
            cellRenderer: GoalStatusCellRenderer,
            maxWidth: 120
        },
        {
            field: "",
            headerName: "",
            filter: false,
            cellRenderer: GoalActionCellRenderer,
            maxWidth: 100
        }
    ]);

    return (
        <div
            className="ag-theme-alpine"
            id="goals-grid"
            style={{
                height: "24rem",
            }}
        >
            <AgGridLayout
                api={gridApi}
                loading={goalsGridLoading}
                onSelectionChanged={() => null}
                rowData={goalData}
                isTotalRowsReq={false}
                setGridApi={setGridApi}
                columnDefs={columnDefs}
            />
        </div>
    );
};

export default GoalsGrid;
